import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FEATURES } from '@yuan-math/config';
import { KINGDOMS, ghosts } from '@yuan-math/content';
import type { Difficulty, GradeResult } from '@yuan-math/exercises';
import { allowanceMessage } from '@yuan-math/learning';
import {
  conceptKingdoms,
  currentQuestion,
  ghostIndex,
  ghostForTrick,
  openingBand,
  replayEncounter,
  sealFor,
  sealTarget,
  siteBattleId,
  siteCleared,
  siteEncounter,
  stepEncounter,
  trickFor,
  SITE_GOLD,
  SITE_QUESTIONS,
  SITE_XP,
  type EncounterQuestion,
  type EncounterRun,
  type Ghost,
  type Seal,
} from '@yuan-math/world';
import { Button, Card, Confetti, Mascot } from '@yuan-math/ui';
import { ExerciseRunner } from '../../components/ExerciseRunner.tsx';
import { LessonBlock } from '../../components/LessonBlocks.tsx';
import { MasteryBand } from '../../components/MasteryBand.tsx';
import { bank, registry, useLearner } from '../../state/LearnerContext.tsx';
import { useRewards } from '../../state/RewardsContext.tsx';
import { NotFoundPage } from '../NotFoundPage.tsx';

/**
 * `/adventure/encounter/:conceptId` — the warded site (docs/rpg.md §16–18, §36).
 *
 * The encounter is the study cycle a student already does — review the idea,
 * work the problems, say what went wrong — wearing the world's clothes. This
 * page is all three beats.
 *
 * **Beat one, the Ward.** The concept's own lesson, opened at its worked
 * example, with the whole page one tap away. No new content and no second
 * explanation: `content/` writes lessons and `LessonBlocks` decides how a block
 * looks, here as everywhere. It is skippable in one press (§21) and a student
 * already mid-fight never sees it, which is also what a reload lands on.
 *
 * **Beat two, the Haunting.** Questions from the concept's own templates,
 * through the ordinary bank and the ordinary grader and the ordinary runner. A
 * wrong answer does not damage anything — it *identifies* the ghost. The grader
 * already names the misconception and `ghosts.ts` already turns that into a
 * creature, so what happens on a miss is that the thing you are fighting takes
 * a shape and a name. The *explanation* stays the runner's job (see
 * `GhostReveal`): the panel says what you are fighting, the feedback beneath it
 * says what to do about it, and neither repeats the other.
 *
 * Five rules hold the rest, and every one of them is somebody else's invariant.
 *
 * **The fight is derived, not stored.** Everything on screen is rebuilt by
 * `replayEncounter` from who the student is, which attempt this is, and how
 * each answer went. The profile keeps the last two. There is no `useState`
 * holding a battle: a copy of the fight in React state is a copy that can
 * disagree with the profile the moment anything else touches it.
 *
 * **The opening band lives in the URL**, for the reason `PracticePage`'s seed
 * does. The band a site opens at comes from the student's mastery — but mastery
 * *moves as they answer*, so re-reading it on the next render would re-seed the
 * questions underneath them. Writing it to the URL on arrival freezes it for
 * the fight and starts it fresh on the next visit. (A fight resumed from a
 * different device, with no band in its URL, therefore replays its score
 * correctly against different problems. It is five free questions and losing
 * costs nothing; storing a band per battle to close that would be a stored fact
 * about a student's mathematics that nothing else needs.)
 *
 * **Every answer counts twice, and neither is optional.** It goes through
 * `recordAttempt` exactly as a practice answer does — moving mastery, XP, the
 * streak and the daily quests — and to `recordBattleAnswer`, the fight's own
 * record. Feeding only the fight would mean a student could spend ten minutes
 * here and have the platform record that they did nothing.
 *
 * **Nothing here withholds mathematics.** A site is a presentation of practice.
 * The concept page, the lesson and `/practice/:conceptId` stay one tap away
 * throughout, including from the defeat screen, and this page never asks
 * whether a student is *allowed* to be at it.
 *
 * **Losing costs nothing** (§12) and **walking away is free**. No HP, no lives,
 * no timer, and nothing stored about a failed attempt. A defeat produces one
 * thing: the ghost's name, and the ward that beats it.
 *
 * **Beat three, the Sealing** (see `Sealing`), offered after a win *and* after
 * a loss — a loss is the moment there is most to name. It never touches
 * mathematics: no XP, no gold, no mastery, no attempt logged. The single fact
 * it writes is one misconception id, for the bestiary.
 */

/** The bestiary is a pure function of static content, so it is built once. */
const BESTIARY = ghostIndex(ghosts);

/** Which kingdom holds a concept. Static content, so also built once. */
const KINGDOM_OF_CONCEPT = conceptKingdoms(KINGDOMS);

/* -------------------------------------------------------------------------- */
/* The dots                                                                   */
/* -------------------------------------------------------------------------- */

type DotOutcome = 'correct' | 'wrong' | 'pending';

const DOT_GLYPH: Record<DotOutcome, string> = { correct: '✓', wrong: '✕', pending: '·' };
const DOT_WORD: Record<DotOutcome, string> = { correct: 'right', wrong: 'wrong', pending: 'not answered yet' };

/**
 * How the fight is going, one dot per question.
 *
 * Colour is never the only carrier (`CLAUDE.md`): each dot is a colour *and* a
 * glyph *and* a word in its own label. There is no timer beside it — the dots
 * report what has happened and nothing counts down.
 */
function EncounterDots({ outcomes }: { outcomes: DotOutcome[] }): JSX.Element {
  const answered = outcomes.filter((outcome) => outcome !== 'pending').length;
  return (
    <ol className="boss-dots" aria-label={`Progress: ${answered} of ${outcomes.length} answered`}>
      {outcomes.map((outcome, index) => (
        <li key={index} className={`boss-dot boss-dot--${outcome} ${index === answered ? 'boss-dot--current' : ''}`}>
          <span aria-hidden="true">{DOT_GLYPH[outcome]}</span>
          <span className="visually-hidden">{`Question ${index + 1}: ${DOT_WORD[outcome]}`}</span>
        </li>
      ))}
    </ol>
  );
}

/* -------------------------------------------------------------------------- */
/* The page                                                                   */
/* -------------------------------------------------------------------------- */

export function EncounterPage(): JSX.Element {
  const { conceptId } = useParams();
  const [params, setParams] = useSearchParams();
  const { profile, masteryOf, recordAttempt, recordBattleAnswer, recordTrickNamed, resetBattle, settleSiteVictory, allowance } =
    useLearner();
  const { celebrate } = useRewards();

  const concept = conceptId ? registry.concept(conceptId) : undefined;
  const kingdomId = conceptId ? KINGDOM_OF_CONCEPT.get(conceptId) : undefined;
  const kingdom = KINGDOMS.find((candidate) => candidate.id === kingdomId);

  const mastery = masteryOf(conceptId ?? '');

  /*
   * The band this fight opened at. See the note at the top of the file: read
   * from the URL if it is there, and otherwise taken from mastery once, at
   * arrival, and written back — because mastery moves as the student answers
   * and a band that moved with it would re-seed the questions mid-fight.
   */
  const [arrivalBand] = useState<Difficulty>(() => openingBand(mastery.overall));
  const urlBand = Number(params.get('band'));
  const band: Difficulty = urlBand >= 1 && urlBand <= 5 ? (urlBand as Difficulty) : arrivalBand;

  useEffect(() => {
    if (params.get('band')) return;
    const next = new URLSearchParams(params);
    next.set('band', String(arrivalBand));
    setParams(next, { replace: true });
  }, [params, arrivalBand, setParams]);

  /*
   * A fixed moment rather than `Date.now()` on every render. `startEncounter`
   * records `now` and never seeds from it, so this only keeps `startedAt`
   * still — but a value that moved every render would defeat the memo below.
   */
  const [openedAt] = useState(() => Date.now());

  const battleId = conceptId ? siteBattleId(conceptId) : '';
  const record = profile?.rpg?.battles?.[battleId];
  const attemptIndex = record?.attemptIndex ?? 0;
  const answers = useMemo(() => record?.answers ?? [], [record?.answers]);

  const run: EncounterRun | undefined = useMemo(() => {
    if (!conceptId || !concept || !profile) return undefined;
    return { def: siteEncounter(conceptId, concept.title, band), bank, learnerId: profile.id, attemptIndex };
  }, [conceptId, concept, profile?.id, attemptIndex, band]);

  const battle = useMemo(
    () => (run ? replayEncounter(run, answers, openedAt) : undefined),
    [run, answers, openedAt],
  );

  /** The Ward, closed once an answer has been given. A reload lands here too. */
  const [reading, setReading] = useState(() => answers.length === 0);

  /**
   * The question being *shown*, when it is no longer the question being asked.
   *
   * The answer is recorded the moment it is graded — so a reload cannot undo a
   * miss — but the explanation underneath it is the whole teaching moment, and
   * swapping the question out from under it would throw that away. The answered
   * question is held on screen until the student presses on, while the fight
   * behind it has already moved. It is also what a *second* try is recorded
   * against: real evidence about this concept, whatever the fight has done.
   */
  const [held, setHeld] = useState<EncounterQuestion | null>(null);

  /** The ghost the last wrong answer revealed, shown beside the explanation. */
  const [revealed, setRevealed] = useState<{ ghost: Ghost; trick: string } | null>(null);

  /** Whether this visit is the one that cleared the site, for the confetti. */
  const [settledHere, setSettledHere] = useState(false);

  const onGraded = useCallback(
    (result: GradeResult, meta: { responseMs: number; firstTry: boolean; usedHint: boolean }) => {
      if (!battle || !conceptId) return;
      // Belt and braces: the runner does not report a malformed answer at all,
      // and the reducer and `applyAttempt` both refuse one after this.
      if (result.status === 'malformed') return;
      const question = held ?? currentQuestion(battle);
      if (!question) return;

      recordAttempt({
        conceptId: question.conceptId,
        exerciseId: question.exercise.id,
        difficulty: question.exercise.difficulty,
        result,
        responseMs: meta.responseMs,
        usedHint: meta.usedHint,
        firstTry: meta.firstTry,
      });

      /*
       * A wrong answer identifies the ghost rather than damaging anything.
       * When the grader names no misconception the honest thing is to say
       * nothing — inventing a diagnosis is worse than admitting there isn't
       * one, the same rule the journal follows.
       */
      const ghost = result.misconceptionId ? ghostForTrick(BESTIARY, result.misconceptionId) : undefined;
      const trick = result.misconceptionId ? trickFor(BESTIARY, result.misconceptionId) : undefined;
      setRevealed(!result.correct && ghost && trick ? { ghost, trick: trick.title } : null);

      /*
       * The fight sees the first answer only. A second try is worth having —
       * the explanation is the point of it — but a fight scored on the last of
       * several tries would not be an assessment of anything.
       */
      if (!meta.firstTry) return;
      recordBattleAnswer({ battleId, attemptIndex, answer: result.correct ? 'correct' : 'wrong' });
      setHeld(question);
    },
    [attemptIndex, battle, battleId, conceptId, held, recordAttempt, recordBattleAnswer],
  );

  const pressOn = useCallback(() => {
    setHeld(null);
    setRevealed(null);
  }, []);

  /* ------------------------------------------------- beat three: the seal -- */

  /**
   * The question that closes the encounter, derived like everything else here.
   *
   * The trick asked about is the one that has actually been catching this
   * student on this concept (`sealTarget` reads the attempt log), and when the
   * run was clean it is a trap the fight's own exercises carried and they did
   * not take — real authored data, never a consolation prize.
   *
   * The seed is the battle, so a student who reloads the outcome screen is
   * offered the same four statements in the same order rather than a quietly
   * re-rolled question.
   */
  const seal = useMemo(() => {
    if (!conceptId || !profile || !battle) return undefined;
    const offered = battle.stages
      .flatMap((stage) => stage.questions)
      .flatMap((question) => question.exercise.traps ?? [])
      .map((trap) => trap.misconceptionId)
      .filter((id): id is string => Boolean(id));

    const target = sealTarget(BESTIARY, profile.attempts, conceptId, offered);
    if (!target) return undefined;
    const built = sealFor(BESTIARY, target.misconceptionId, `${battleId}:${attemptIndex}`);
    return built ? { ...built, taken: target.taken } : undefined;
  }, [attemptIndex, battle, battleId, conceptId, profile]);

  /** What the student picked this visit, so the panel can answer them. */
  const [named, setNamed] = useState<{ chosen: string; correct: boolean } | null>(null);

  const onName = useCallback(
    (misconceptionId: string) => {
      if (!seal || named) return;
      const correct = misconceptionId === seal.misconceptionId;
      setNamed({ chosen: misconceptionId, correct });
      /*
       * Recorded only when they got it right, and recorded *nowhere near*
       * mastery: `recordTrickNamed` writes one id into the RPG slice and
       * touches nothing else. Getting it wrong shows the true statement and
       * costs nothing — a seal is a reflection, not a second grading, and
       * running it through mastery would be the same category error as
       * counting a typo as a wrong answer.
       */
      if (correct) recordTrickNamed(seal.misconceptionId);
    },
    [named, recordTrickNamed, seal],
  );

  const onRetry = useCallback(() => {
    if (!battleId) return;
    resetBattle(battleId);
    setHeld(null);
    setRevealed(null);
    setReading(false);
    setSettledHere(false);
    settled.current = false;
  }, [battleId, resetBattle]);

  /*
   * Settling the win.
   *
   * In an effect because it writes to the profile, and paid once beyond that
   * because the claim is idempotent — a site fought again is warm and pays
   * nothing. No gold, no XP, and no confetti, which is the part that keeps
   * confetti meaning something (`CLAUDE.md` — celebration is rationed).
   */
  const won = battle?.outcome === 'won' && held === null;
  const alreadyCleared = conceptId && profile ? siteCleared(conceptId, profile) : false;
  const settled = useRef(false);
  useEffect(() => {
    if (!conceptId || !profile || !won || settled.current) return;
    settled.current = true;
    /*
     * Read *before* settling: afterwards the site is claimed either way, so
     * this is the only moment that can tell a first clear from a re-fight.
     */
    const first = !alreadyCleared;
    settleSiteVictory(conceptId, 'won');
    if (!first) return;
    setSettledHere(true);
    celebrate({
      key: `site-${conceptId}`,
      tone: 'quest',
      icon: '👻',
      title: 'The ward holds',
      detail: `+${SITE_GOLD} gold`,
      xp: SITE_XP,
    });
  }, [alreadyCleared, celebrate, conceptId, profile, settleSiteVictory, won]);

  if (!concept || !conceptId) return <NotFoundPage />;

  const backToKingdom = kingdom ? `/adventure/kingdoms/${kingdom.id}` : '/adventure/map';

  /* ------------------------------------------------------------ the cap --- */

  /*
   * The free daily cap, checked here for the same reason `PracticePage` checks
   * it: this page *generates problems*, and that is the only thing the plans
   * ever limit. It never limits the lesson below, the concept page, the map or
   * the student's own progress — the Ward stays open and so does everything it
   * links to.
   */
  if (allowance.exhausted && answers.length === 0) {
    return (
      <div className="page page--narrow encounter-page">
        <EncounterBreadcrumb concept={concept.title} conceptId={conceptId} kingdom={kingdom?.name} to={backToKingdom} />
        <h1 className="page__title">The site is quiet today.</h1>
        <p className="page__lead">{allowanceMessage(allowance)}</p>
        <Card>
          <p>
            You answered {allowance.usedToday} questions today. The reading is still here, and so is everything it
            points at — come back tomorrow and the ghost will still be waiting.
          </p>
        </Card>
        <div className="button-row">
          <Link to={`/concepts/${conceptId}`} className="btn btn--primary">
            Back to {concept.title}
          </Link>
          <Link to={backToKingdom} className="btn btn--secondary">
            {kingdom ? `Back to ${kingdom.name}` : 'World map'}
          </Link>
          {FEATURES.payments ? (
            <Link to="/pricing" className="btn btn--ghost">
              See the plans
            </Link>
          ) : null}
        </div>
      </div>
    );
  }

  if (!battle) return <div />;

  /* ------------------------------------------------------- beat one: ward -- */

  if (reading && battle.outcome === 'fighting') {
    return (
      <div className="page page--narrow encounter-page">
        <EncounterBreadcrumb concept={concept.title} conceptId={conceptId} kingdom={kingdom?.name} to={backToKingdom} />
        <Ward
          conceptId={conceptId}
          conceptTitle={concept.title}
          summary={concept.summary}
          band={band}
          level={mastery.level}
          onEnter={() => setReading(false)}
          backTo={backToKingdom}
          backLabel={kingdom ? `Back to ${kingdom.name}` : 'World map'}
        />
      </div>
    );
  }

  /* --------------------------------------------------------- the outcome -- */

  if (battle.outcome === 'won' && held === null) {
    return (
      <div className="page page--narrow encounter-page">
        <EncounterBreadcrumb concept={concept.title} conceptId={conceptId} kingdom={kingdom?.name} to={backToKingdom} />
        <Card>
          <div className="encounter-outcome">
            <Mascot mood="celebrate" size={96} />
            <h1 className="page__title">The ward holds.</h1>
            <p className="page__lead">
              You answered {battle.stages[0]?.correct} of {battle.stages[0]?.questionCount} on {concept.title}.
            </p>
            {settledHere ? <Confetti /> : null}
            <p className="encounter-outcome__note">
              {alreadyCleared && !settledHere
                ? 'This site was already cleared, so it pays nothing more — but the answers counted, exactly as practice does.'
                : `+${SITE_XP} XP · +${SITE_GOLD} gold, once ever. Every answer here also moved your mastery.`}
            </p>
          </div>
        </Card>
        <Sealing seal={seal} named={named} onName={onName} />
        <OutcomeLinks conceptId={conceptId} conceptTitle={concept.title} backTo={backToKingdom} kingdom={kingdom?.name} onRetry={onRetry} />
      </div>
    );
  }

  if (battle.outcome === 'lost' && held === null) {
    const missed = battle.stages[0]?.wrong ?? 0;
    return (
      <div className="page page--narrow encounter-page">
        <EncounterBreadcrumb concept={concept.title} conceptId={conceptId} kingdom={kingdom?.name} to={backToKingdom} />
        <Card>
          <div className="encounter-outcome">
            {/*
             * Thoughtful rather than sad. A fight that went badly is a fight
             * that taught something, and a miserable face at the end of it is
             * the fastest way to make a child stop coming back.
             */}
            <Mascot mood="thinking" size={96} />
            <h1 className="page__title">It slipped away.</h1>
            <p className="page__lead">
              {missed} {missed === 1 ? 'answer' : 'answers'} went astray on {concept.title}. That is the whole cost —
              nothing was lost, and every answer still counted towards your mastery.
            </p>
          </div>
        </Card>
        <Sealing seal={seal} named={named} onName={onName} />
        <OutcomeLinks conceptId={conceptId} conceptTitle={concept.title} backTo={backToKingdom} kingdom={kingdom?.name} onRetry={onRetry} />
      </div>
    );
  }

  if (battle.outcome === 'retreated') {
    return (
      <div className="page page--narrow encounter-page">
        <EncounterBreadcrumb concept={concept.title} conceptId={conceptId} kingdom={kingdom?.name} to={backToKingdom} />
        <Card>
          <p>You stepped back. Nothing was lost — the site will be here.</p>
        </Card>
        <OutcomeLinks conceptId={conceptId} conceptTitle={concept.title} backTo={backToKingdom} kingdom={kingdom?.name} onRetry={onRetry} />
      </div>
    );
  }

  /* ---------------------------------------------------- beat two: the fight - */

  const question = currentQuestion(battle);
  const stage = battle.stages[0];
  const exercise = held?.exercise ?? question?.exercise;
  if (!stage || !exercise) return <div />;

  const outcomes: DotOutcome[] = Array.from(
    { length: stage.questionCount },
    (_unused, index) => answers[index] ?? 'pending',
  );

  return (
    <div className="page page--narrow encounter-page">
      <EncounterBreadcrumb concept={concept.title} conceptId={conceptId} kingdom={kingdom?.name} to={backToKingdom} />

      <header className="boss-stage">
        <p className="boss-stage__label">
          {concept.title} · difficulty {(held ?? question)?.difficulty ?? stage.difficulty} of 5
        </p>
        <h2 className="boss-stage__name">The warded site</h2>
        <EncounterDots outcomes={outcomes} />
      </header>

      {revealed ? <GhostReveal ghost={revealed.ghost} trick={revealed.trick} /> : null}

      {/*
       * The ordinary runner, unchanged. It knows how to ask a question, grade
       * it, explain it and offer the next one — and forking it here would
       * create a second place answers are graded, which within a release would
       * disagree with the first about something.
       */}
      <ExerciseRunner exercise={exercise} onGraded={onGraded} showNext onNext={pressOn} />

      <div className="boss-page__footer">
        <Link to={`/lessons/${registry.lessonsForConcept(conceptId)[0]?.id ?? ''}`} className="btn btn--ghost btn--sm">
          Read the ward
        </Link>
        <Link to={backToKingdom} className="btn btn--ghost btn--sm">
          Step back
        </Link>
        <p className="boss-page__note">
          Stepping back costs nothing and keeps your place. There is no timer here.
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Beat one: the Ward                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The concept's own lesson, opened at its worked example.
 *
 * Not a second explanation — content is data and `LessonBlocks` is the only
 * file that decides how a block looks (`CLAUDE.md`). If the lesson has no
 * worked example the section is simply absent and the link stands alone, which
 * is the honest behaviour rather than a fabricated summary.
 */
function Ward({
  conceptId,
  conceptTitle,
  summary,
  band,
  level,
  onEnter,
  backTo,
  backLabel,
}: {
  conceptId: string;
  conceptTitle: string;
  summary: string;
  band: Difficulty;
  level: Parameters<typeof MasteryBand>[0]['level'];
  onEnter: () => void;
  backTo: string;
  backLabel: string;
}): JSX.Element {
  const lesson = registry.lessonsForConcept(conceptId)[0];
  const worked = lesson?.sections.find((section) => section.kind === 'worked-example');

  return (
    <>
      <header className="encounter-header">
        <span className="encounter-header__emoji" aria-hidden="true">
          🕯️
        </span>
        <div>
          <h1 className="page__title">{conceptTitle}</h1>
          <p className="encounter-header__meta">
            A warded site · {SITE_QUESTIONS} questions · opening at difficulty {band} of 5 · no timer
          </p>
          <MasteryBand level={level} />
        </div>
      </header>

      <p className="page__lead">{summary}</p>

      {worked ? (
        <Card>
          <h2 className="encounter-ward__title">{worked.title}</h2>
          {worked.blocks.map((block, index) => (
            <LessonBlock key={index} block={block} />
          ))}
        </Card>
      ) : null}

      <div className="button-row">
        <Button onClick={onEnter}>Face what is here</Button>
        {lesson ? (
          <Link to={`/lessons/${lesson.id}`} className="btn btn--secondary">
            Read the whole lesson
          </Link>
        ) : null}
        <Link to={`/practice/${conceptId}`} className="btn btn--ghost">
          Just practise
        </Link>
        <Link to={backTo} className="btn btn--ghost">
          {backLabel}
        </Link>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Furniture                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * What a wrong answer produces: a name, the trick, and the ward that beats it.
 *
 * **It deliberately does not say what the runner is about to say.** The
 * catalog's `studentMessage` — "Multiplication and division happen before
 * addition and subtraction, whatever the order on the page" — is the single
 * most teachable line the platform generates, and the ordinary exercise runner
 * already gives it, right below this. Repeating it here would put the same
 * sentence on the screen twice and make the ghost look like decoration on top
 * of the feedback rather than the thing the feedback is about.
 *
 * So this adds what the runner cannot: an *identity*. The short `title` names
 * the trick, the ward is a link into the mathematics that repairs it, and the
 * name is the same one the student will meet again the next time they make this
 * mistake — in this kingdom or five years further up the graph.
 */
function GhostReveal({ ghost, trick }: { ghost: Ghost; trick: string }): JSX.Element {
  return (
    <aside className="encounter-ghost" aria-live="polite">
      <span className="encounter-ghost__emoji" aria-hidden="true">
        {ghost.emoji}
      </span>
      <div>
        <p className="encounter-ghost__name">{ghost.name}</p>
        <p className="encounter-ghost__whisper">Its trick: {trick.charAt(0).toLowerCase()}{trick.slice(1)}.</p>
        <p className="encounter-ghost__ward">
          Warded by <Link to={`/concepts/${ghost.wardConceptId}`}>{ghost.wardTitle}</Link>
        </p>
      </div>
    </aside>
  );
}

/**
 * Beat three — the Sealing.
 *
 * The one place on this platform that asks a student to say what an *error is*
 * rather than what an answer is, and the reason the encounter is a study cycle
 * rather than a themed practice set. Four statements, one true; the true one is
 * the mistake that has actually been catching them here, or — after a clean run
 * — the trap the questions carried and they never took.
 *
 * Three rules, and all three are about not turning reflection into assessment.
 *
 * **It never touches mathematics.** No XP, no gold, no mastery, no attempt
 * logged. Getting it wrong shows the true statement and seals anyway. The only
 * thing a correct answer writes is one id, for the bestiary's tick.
 *
 * **It is skippable, and skipping is a button rather than a shrug.** A student
 * who wants their reward and their next lesson is not made to sit a quiz for
 * it; the reward was already paid on the outcome screen above.
 *
 * **The statements are the catalog's own third-person `description`.** The
 * student is describing what the creature does, not confessing — four
 * second-person accusations in a list would read very differently, and the
 * choice of `description` over `studentMessage` is the whole difference.
 */
function Sealing({
  seal,
  named,
  onName,
}: {
  seal: (Seal & { taken: boolean }) | undefined;
  named: { chosen: string; correct: boolean } | null;
  onName: (misconceptionId: string) => void;
}): JSX.Element | null {
  const [skipped, setSkipped] = useState(false);
  // Nothing to ask about honestly — no mistake made, no trap laid — so the beat
  // is skipped rather than invented. Same rule the journal follows.
  if (!seal || skipped) return null;

  const truth = seal.choices.find((choice) => choice.correct);

  return (
    <Card>
      <section className="sealing" aria-labelledby="sealing-heading">
        <h2 className="sealing__heading" id="sealing-heading">
          {seal.taken ? 'Name what caught you' : 'Name the snare you stepped over'}
        </h2>
        <p className="sealing__lead">
          {seal.taken
            ? 'This is the trick that has been working on you here lately. Which one is it?'
            : 'You never took it — but it was laid, more than once. Which one was it?'}
        </p>

        {named === null ? (
          <ul className="sealing__choices">
            {seal.choices.map((choice) => (
              <li key={choice.misconceptionId}>
                <button
                  type="button"
                  className="sealing__choice"
                  onClick={() => onName(choice.misconceptionId)}
                >
                  {choice.text}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className={`sealing__verdict sealing__verdict--${named.correct ? 'named' : 'missed'}`}>
            {/*
             * A word and an icon, never a colour alone (`CLAUDE.md`). And the
             * "missed" wording is deliberately not a wrong-answer wording: the
             * student is being shown the trick, not marked down for it.
             */}
            <p className="sealing__result">
              <span aria-hidden="true">{named.correct ? '🔒' : '👁️'}</span>{' '}
              {named.correct ? 'Sealed. That is exactly its trick.' : 'Not quite — here it is.'}
            </p>
            <p className="sealing__truth">{truth?.text}</p>
            <p className="sealing__note">
              Naming a mistake is not the same as having stopped making it, so this changed no mastery and paid no
              XP. It goes in your bestiary.
            </p>
          </div>
        )}

        {named === null ? (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSkipped(true)}>
            Skip this
          </button>
        ) : null}
      </section>
    </Card>
  );
}

function EncounterBreadcrumb({
  concept,
  conceptId,
  kingdom,
  to,
}: {
  concept: string;
  conceptId: string;
  kingdom?: string;
  to: string;
}): JSX.Element {
  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <Link to="/adventure/map">World map</Link>
      <span aria-hidden="true">/</span>
      {kingdom ? <Link to={to}>{kingdom}</Link> : <span>Adventure</span>}
      <span aria-hidden="true">/</span>
      <Link to={`/concepts/${conceptId}`}>{concept}</Link>
    </nav>
  );
}

/**
 * Where a finished fight lets a student go.
 *
 * Deliberately four ways out, and three of them are mathematics rather than
 * story: the concept page, ordinary practice, and the fight again. A site that
 * ended by offering only "back to the kingdom" would be the adventure closing a
 * door the platform promises never to close.
 */
function OutcomeLinks({
  conceptId,
  conceptTitle,
  backTo,
  kingdom,
  onRetry,
}: {
  conceptId: string;
  conceptTitle: string;
  backTo: string;
  kingdom?: string;
  onRetry: () => void;
}): JSX.Element {
  return (
    <div className="button-row">
      <Button onClick={onRetry}>Face it again</Button>
      <Link to={`/concepts/${conceptId}`} className="btn btn--secondary">
        {conceptTitle}
      </Link>
      <Link to={`/practice/${conceptId}`} className="btn btn--ghost">
        Practise
      </Link>
      <Link to={backTo} className="btn btn--ghost">
        {kingdom ? `Back to ${kingdom}` : 'World map'}
      </Link>
    </div>
  );
}
